import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf,
  bool,
  customType,
  extend,
  params,
  string as stringType
} from '@thd-nucleus/data-sources';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { getPageType } from '../../../utils/window-utils';
import { getRandomElements, getRandomQuestions } from '../../../utils/common';
import { GENERAL_QUESTIONS, PIP_QUESTIONS } from './constants';
import { useAssistant } from '../AssistantProvider';
import { TileButton } from '../../common/TileButton';
import { AssistantLoader } from './drawer/AssistantLoader';
import { PAGE_TYPES } from '../constants';

// Random suggested questions logic was moved outside the component to get the same questions for all instances
const randomSuggestQuestions = getRandomElements(getPageType() === PAGE_TYPES.PIP ? PIP_QUESTIONS : GENERAL_QUESTIONS);

const SuggestedQuestions = ({ onClick, withAskPrompt, handleAskPromptClick, truncate }) => {
  const {
    isConsumerApp,
    channel
  } = useContext(ExperienceContext);
  const { messages, isSuggestedQuestionsLoading, wereQuestionsGenerated } = useAssistant();

  const isDynamicIsqMasterEnabled = useConfigService('magic-apron-initial-suggested-questions-enabled');
  const isDynamicIsqDtFsEnabled = useConfigService('magic-apron-dynamic-isq-dt-enabled');
  const isDynamicIsqMwFsEnabled = useConfigService('magic-apron-dynamic-isq-mw-enabled');
  const isDynamicIsqAppFsEnabled = useConfigService('magic-apron-dynamic-isq-app-enabled');
  const isStaticDealQuestionsEnabled = useConfigService('magic-apron-isq-static-deals-enabled');
  const staticDealQuestionsFS = useConfigService('magic-apron-static-deal-questions') || null;

  const staticQuestions = getRandomQuestions({
    randomSuggestQuestions,
    isDynamicIsqMasterEnabled,
    isDynamicIsqDtFsEnabled,
    isDynamicIsqMwFsEnabled,
    isDynamicIsqAppFsEnabled,
    isStaticDealQuestionsEnabled,
    staticDealQuestionsFS,
    channel,
    isConsumerApp
  });

  const lastMessage = messages[messages.length - 1];
  const displayQuestions = lastMessage?.content?.suggestedQuestions?.length > 0
    ? lastMessage.content.suggestedQuestions
    : staticQuestions;

  return wereQuestionsGenerated && (
    isSuggestedQuestionsLoading
      ? (<AssistantLoader loading={isSuggestedQuestionsLoading} />)
      : (
        <div
          // eslint-disable-next-line max-len
          className="sui-flex sui-gap-4 sui-content-stretch sui-mb-4 [@media(min-height:101px)]:sui-mt-2 sui-items-start [@media(max-height:100px)]:sui-flex-row [@media(max-height:100px)]:sui-px-4 [@media(max-height:100px)]:sui-overflow-x-scroll [@media(min-height:101px)]:sui-flex-col"
          data-testid="suggested-questions"
        >
          {withAskPrompt && (
            <div className="sui-flex sui-shrink-0 sui-grow">
              <TileButton
                onClick={handleAskPromptClick}
                value="Ask a question"
                data-testid="suggested-question"
                truncate={truncate}
              />
            </div>
          )}
          {displayQuestions.map((question, i) => (
            <div className="sui-flex sui-shrink-0 sui-grow" key={i}>
              <TileButton
                onClick={() => onClick(question, i, lastMessage?.isInitialMessage)}
                value={question}
                data-testid="suggested-question"
                truncate={truncate}
                withIcon
              />
            </div>
          ))}
        </div>
      )
  );
};

SuggestedQuestions.propTypes = {
  withAskPrompt: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  handleAskPromptClick: PropTypes.func,
  truncate: PropTypes.bool,
};

SuggestedQuestions.defaultProps = {
  withAskPrompt: false,
  handleAskPromptClick: () => {},
  truncate: false,
};

SuggestedQuestions.dataModel = extend(
  {
    generateQuestions: params({
      userContext: customType('UserContext')
        .shape({
          userIdentities: customType('UserIdentities')
            .shape({
              mcvisId: stringType(),
              svocId: stringType()
            }),
          sessionId: stringType(),
          storeId: stringType(),
          zipCode: stringType(),
          deviceType: stringType(),
          language: stringType()
        }),
      pageContext: customType('PageContext')
        .shape({
          pageType: stringType(),
          referrerPage: stringType(),
          timestamp: stringType(),
          url: stringType(),
        }),
      questionContext: customType('QuestionContext')
        .shape({
          userQuestion: stringType(),
          user_typed: stringType(),
          itemId: stringType(),
          recentSearchTerms: arrayOf(stringType()),
        }),
      suggestedQuestions: bool(),
      followUpQuestions: bool(),
      model_name: stringType(),
    })
      .mutation()
      .shape({
        suggestedQuestions: arrayOf(stringType()),
        additionalDetails: customType('MagicApronAdditionalDetails')
          .shape({
            inquiryDetails: customType('MagicApronInquiryDetails')
              .shape({
                telephoneNumber: stringType(),
                description: stringType(),
                referenceUrl: stringType()
              })
          }),
      })
  });

export { SuggestedQuestions };
